// utils/checkZigzag.js
import * as Cesium from "cesium";

// Helper: remove previous zigzag highlights
function removeZigzagHighlights(dataSource) {
  if (!dataSource || !dataSource.entities) return;
  const entities = dataSource.entities.values.slice();
  entities.forEach((entity) => {
    if (entity._zigzagHighlight) {
      dataSource.entities.remove(entity);
    }
  });
}

// Helper: check if two Cartesian points are almost the same (tolerance ~ 0.005 m)
function arePointsClose(p1, p2) {
  if (!p1 || !p2) return false;
  const d = Cesium.Cartesian3.distance(p1, p2);
  return d < 0.005;
}

export function checkZigzagOnDataSource(dataSource, zigzagAngleDeg, reset = false, callback, betweenEntities = false) {
  if (!dataSource || !dataSource.entities) return;

  if (reset) {
    removeZigzagHighlights(dataSource);
    if (callback) callback([]);
    return;
  }

  removeZigzagHighlights(dataSource);

  const entities = dataSource.entities.values || [];
  const now = Cesium.JulianDate.now();
  const results = [];

  // Helper: compute angle and push result if it exceeds threshold
  function addZigzagIfNeeded(p0, p1, p2, info) {
    if (!p0 || !p1 || !p2) return;

    const c0 = Cesium.Cartographic.fromCartesian(p0);
    const c1 = Cesium.Cartographic.fromCartesian(p1);
    const c2 = Cesium.Cartographic.fromCartesian(p2);

    const R = 6378137.0;
    const cosLat = Math.cos(c1.latitude);

    const x0 = c0.longitude * cosLat * R;
    const y0 = c0.latitude * R;
    const x1 = c1.longitude * cosLat * R;
    const y1 = c1.latitude * R;
    const x2 = c2.longitude * cosLat * R;
    const y2 = c2.latitude * R;

    const v1x = x1 - x0;
    const v1y = y1 - y0;
    const v2x = x2 - x1;
    const v2y = y2 - y1;

    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 < 1 || len2 < 1) return;

    let cosAngle = (v1x * v2x + v1y * v2y) / (len1 * len2);
    cosAngle = Math.max(-1, Math.min(1, cosAngle));
    const angleRad = Math.acos(cosAngle);
    const angleDeg = Cesium.Math.toDegrees(angleRad); // 0 = straight, 180 = U-turn

    if (angleDeg >= zigzagAngleDeg) {
      const lons = [Cesium.Math.toDegrees(c0.longitude), Cesium.Math.toDegrees(c1.longitude), Cesium.Math.toDegrees(c2.longitude)];
      const lats = [Cesium.Math.toDegrees(c0.latitude), Cesium.Math.toDegrees(c1.latitude), Cesium.Math.toDegrees(c2.latitude)];

      const west = Math.min(...lons);
      const east = Math.max(...lons);
      const south = Math.min(...lats);
      const north = Math.max(...lats);

      results.push({
        west,
        south,
        east,
        north,
        deviationAngleDeg: angleDeg,
        ...info,
      });
    }
  }

  if (!betweenEntities) {
    // Mode: inside each entity
    entities.forEach((entity) => {
      if (entity._zigzagHighlight) return;

      const polyline = entity.polyline;
      if (!polyline || !polyline.positions) return;

      const positions = polyline.positions.getValue(now);
      if (!positions || positions.length < 3) return;

      for (let i = 1; i < positions.length - 1; i++) {
        const p0 = positions[i - 1];
        const p1 = positions[i];
        const p2 = positions[i + 1];

        addZigzagIfNeeded(p0, p1, p2, {
          entity,
          indices: [i - 1, i, i + 1],
          mode: "withinEntity",
        });
      }
    });
  } else {
    // Mode: between entities (optimized with spatial hash on endpoints)
    const prepared = [];

    entities.forEach((entity) => {
      const polyline = entity.polyline;
      if (!polyline || !polyline.positions) return;

      const positions = polyline.positions.getValue(now);
      if (!positions || positions.length < 2) return;

      prepared.push({ entity, positions });
    });

    const tol = 0.005; // meter
    const cellSize = tol * 2.0; // ensure any pair with dist < tol fall into same bucket
    const buckets = new Map();

    function addEndpointToBucket(item, idx) {
      const p = item.positions[idx];
      if (!p) return;

      // p.x, p.y, p.z are in meters (ECEF)
      const keyX = Math.round(p.x / cellSize);
      const keyY = Math.round(p.y / cellSize);
      const key = keyX + "_" + keyY;

      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({
        entity: item.entity,
        positions: item.positions,
        idx,
      });
    }

    prepared.forEach((item) => {
      const len = item.positions.length;
      addEndpointToBucket(item, 0); // start
      addEndpointToBucket(item, len - 1); // end
    });

    buckets.forEach((list) => {
      const n = list.length;
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const a = list[i];
          const b = list[j];

          const pa = a.positions[a.idx];
          const pb = b.positions[b.idx];
          if (!arePointsClose(pa, pb)) continue;

          const aLen = a.positions.length;
          const bLen = b.positions.length;

          const aIsStart = a.idx === 0;
          const aIsEnd = a.idx === aLen - 1;
          const bIsStart = b.idx === 0;
          const bIsEnd = b.idx === bLen - 1;

          // A end -> shared -> B start (same direction)
          if (aIsEnd && bIsStart && aLen >= 2 && bLen >= 2) {
            const p0 = a.positions[aLen - 2];
            const p1 = a.positions[aLen - 1]; // shared
            const p2 = b.positions[1];

            addZigzagIfNeeded(p0, p1, p2, {
              entityA: a.entity,
              entityB: b.entity,
              indicesA: [aLen - 2, aLen - 1],
              indicesB: [0, 1],
              sharedIndexA: aLen - 1,
              sharedIndexB: 0,
              mode: "betweenEntities",
            });
          }

          // B end -> shared -> A start (same direction)
          if (bIsEnd && aIsStart && bLen >= 2 && aLen >= 2) {
            const p0 = b.positions[bLen - 2];
            const p1 = b.positions[bLen - 1]; // shared
            const p2 = a.positions[1];

            addZigzagIfNeeded(p0, p1, p2, {
              entityA: b.entity,
              entityB: a.entity,
              indicesA: [bLen - 2, bLen - 1],
              indicesB: [0, 1],
              sharedIndexA: bLen - 1,
              sharedIndexB: 0,
              mode: "betweenEntities",
            });
          }
        }
      }
    });
  }

  if (callback) callback(results);
}
